import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { cloneDeep, isEqual } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  first,
  map,
  Observable,
  pairwise,
  startWith,
  Subscription,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';
import { ParticipantsManagerService } from 'src/app/services/participants-manager.service';
import { PresenceProvider } from 'src/app/services/presence-provider';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { VideoCallTracksStateService } from 'src/app/services/video-call-tracks-state.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { TranslateService } from '@ngx-translate/core';
import { PermissionStatus } from 'src/app/consts';
import { getUpdatedPermissions } from 'src/app/common/utils/common-util';
import {
  IconBackground,
  IconMessageToasterElement,
} from 'src/app/ui/notification-toaster/icon-message-toaster-element/icon-message-toaster-element.component';
import {
  NotificationDataBuilder,
  NotificationIconTypes,
  NotificationToasterService,
  NotificationType,
  V2NotificationDataBuilder,
  NotificationIconConfig,
} from 'src/app/services/notification-toaster.service';
import { SUCCESSES, WARNINGS } from 'src/app/common/utils/notification-constants';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { DeviceType } from 'src/app/common/utils/devices-handle-util';
import { AttendanceManagerService } from 'src/app/services/attendance-manager.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SpacesService } from 'src/app/services/spaces.service';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { GlobalPermissionsService } from 'src/app/services/global-permissions.service';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { PencilButtonThemeEnum } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import {
  CursorsControl,
  ISession,
  Permissions,
  Room,
  Session,
  SessionUser,
  Visibility,
} from '../../../models/session';
import { Institution, OnlineStatus, User, UserData } from '../../../models/user';
import { FLAGS, FlagsService } from '../../../services/flags.service';
import { CallAndPresencesService } from '../../../sessions/session/wb-top-controls-v2/services/call-and-presences.service';
import { RealtimeSpaceService } from '../../../services/realtime-space.service';
import { SessionCallTracksService } from '../../../services/session-call-tracks.service';
import { SessionSharedDataService } from '../../../services/session-shared-data.service';
import { UserService } from '../../../services/user.service';
import { EventCategory, SessionEvent } from '../../session/SessionEvent';
import { PaywallIndicatorService } from '../../../services/paywall-indicator.service';
import { ToasterPopupStyle } from '../../../ui/notification-toaster/custom-notification-toastr/custom-notification-toastr.component';
import { RequestAccessService } from '../../session/request-access/request-access.service';
import { WaitingRoomService } from '../../session/request-access/waiting-room/waiting-room.service';
import { CursorControlsService } from '../../session/services/cursor-controls.service';
import { ChildModeService } from '../../../services/child-mode.service';
import { SpaceSettingsUpdateService } from '../../../services/space-settings-update.service';
import { modifiedSetTimeout } from '../../../utilities/ZoneUtils';
import { RaiseHandUiService } from '../../session/services/raise-hand-ui.service';

export enum Section {
  CONTROLS = 'CONTROLS',
  PERMISSIONS = 'PERMISSIONS',
  ATTENDANCE = 'ATTENDANCE',
  QUEUE = 'QUEUE',
}

enum Global_State {
  ENABLED = 'ENABLED', // enabled for all participants
  DISABLED = 'DISABLED', // disabled for all participants
  INDETERMINATE = 'INDETERMINATE', // enabled for some and disabled for others
}

export enum ParticipantsManagerEvent {
  TURN_ON_REQUEST = 'turn_on_request',
  TURNOFF = 'turnoff',
  TURN_ON_REQUEST_ALL = 'turn_on_request_all',
  TURNOFF_ALL = 'turnoff_all',
  TURNON_RECORD = 'turnon_record',
  TURNOFF_RECORD = 'turnoff_record',
  LOWER_HAND = 'lower_hand',
  LOWER_HAND_ALL = 'lower_hand_all',
}

export enum DEVICE {
  MIC = 'mic',
  CAM = 'cam',
}

interface IPermission {
  fieldName:
    | 'shareAudio'
    | 'shareVideo'
    | 'screenShare'
    | 'editSpace'
    | 'messageEveryone'
    | 'messageOtherParticipants'
    | 'createBoards'
    | 'insertWebViewer';
  icon?: string;
  svg?: string;
  name: string;
  dataName: string;
}

@UntilDestroy()
@Component({
  selector: 'app-participants-manager',
  templateUrl: './participants-manager.component.html',
  styleUrls: ['./participants-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantsManagerComponent implements OnInit, AfterViewInit, OnDestroy {
  IconTypes = IconTypes;
  Visibility = Visibility;
  OnlineStatus = OnlineStatus;
  section = Section;
  rolesMap: { [key: string]: SessionUser } = {};
  subscribers: Subscription[] = [];
  activeHostUsers: User[] = [];
  activeParticipantsUsers: User[] = [];
  inactiveUsers: User[] = [];
  currentUser?: User;
  sessionId = '';
  deviceEnum = DEVICE;
  isHost = false;
  // represents if the permission is enabled or not
  currentPermissions: any = new Permissions();
  originalPermissions = new Permissions();

  // represents the state of the permission.
  permissionsState = {
    editSpace: Global_State.ENABLED,
    shareAudio: Global_State.ENABLED,
    shareVideo: Global_State.ENABLED,
    screenShare: Global_State.ENABLED,
  };
  permissionStatus = PermissionStatus;
  globalState = Global_State;
  permissionsChanged = false;
  topbarheight$ = this.sessionSharedDataService.topControlsHeight$;
  securitySectionChanged = false;

  private _currentCursorsPermissson = new BehaviorSubject<CursorsControl>(
    this.spaceRepo.spaceCursorsPermission,
  );

  spaceCursorsPermission$ = this._currentCursorsPermissson.asObservable();
  selectedCursorsControlLabelAndSubtitle$ = this._currentCursorsPermissson.pipe(
    distinctUntilChanged(),
    map((cursorsControl) => this.CursorsControlOptions[cursorsControl]),
  );

  cursorPermissionChanged$ = combineLatest([
    this._currentCursorsPermissson,
    this.spaceRepo.spaceCursorsPermission$,
  ]).pipe(
    map(
      ([currentCursorsPermission, spaceCursorsPermission]) =>
        currentCursorsPermission !== spaceCursorsPermission,
    ),
    distinctUntilChanged(),
  );
  selectedVisibility$: BehaviorSubject<Visibility | undefined> = new BehaviorSubject<
    Visibility | undefined
  >(this.spaceRepo.activeSpace?.visibility);
  selectedWaitingRoom$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    this.globalPermissionsService.isWaitingRoomEnabled,
  );
  isWaitingRoomEnforced$: Observable<boolean>;
  selectedChildMode$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    !!this.spaceRepo.activeSpace?.settings?.enableChildMode,
  );
  readonly isChildModeEnabled$ = this.selectedChildMode$.asObservable();
  /** Staged value of "people from other organizations may message the agent",
   *  applied with the other security settings on save. */
  readonly selectedExternalAgentMessages$ = new BehaviorSubject<boolean>(
    !!this.spaceRepo.activeSpace?.settings?.allowExternalAgentMessages,
  );
  /** The setting only means something where the space's organization has the agent,
   *  and only a host from that organization may change it (the server checks too). */
  readonly showExternalAgentMessagesRow$ = combineLatest([
    this.spaceRepo.activeSpace$,
    this.userService.user,
  ]).pipe(
    map(([space, userData]) => {
      const idOf = (ref: unknown): string | undefined =>
        typeof ref === 'string' ? ref : (ref as { _id?: string } | undefined)?._id;
      const spaceInstitution = idOf(space?.institution);
      return (
        !!space?.institution?.aiAgentEnabled &&
        !!spaceInstitution &&
        spaceInstitution === idOf(userData?.user?.institution)
      );
    }),
  );
  persistentPermissionEnabled$ = this.flagsService
    .featureFlagChanged(FLAGS.BREAKOUT_ROOMS)
    .pipe(untilDestroyed(this));

  isFollowModeFlagEnabled$ = this.flagsService.featureFlagChanged(FLAGS.ENABLE_FOLLOW_MODE);

  currentPresentUsers: Set<string> = new Set();
  inCallUsers: Set<string> = new Set<string>();
  inCallParticipants = 0;
  presenceSub?: Subscription;
  isCallConnected = false;
  isCallReady = false;
  protected readonly areCallFeaturesAvailable: boolean;
  currentRoom?: Room;
  currentBreakoutRoomId?: string;
  activeRoomUsers: SessionUser[] = [];
  selectedPermission: IPermission | undefined;
  usersMap: { [key: string]: User } = {};
  isWebViewerCapabilityEnabled = this.featureManagementService.isWebViewerEnabled();
  public readonly CursorsControl = CursorsControl;
  public readonly CursorsControlOptions = this.cursorControlsService.CursorsControlOptions;

  permissionsGroupMetadata = [
    {
      name: this.translateService.instant('Video/Call controls'),
      icon: 'videocam',
      permissions: [
        {
          fieldName: 'shareAudio',
          name: this.translateService.instant('Turn on microphone'),
          dataName: 'microphone-checkbox',
        },
        {
          fieldName: 'shareVideo',
          name: this.translateService.instant('Turn on camera'),
          dataName: 'camera-checkbox',
        },
        {
          fieldName: 'screenShare',
          name: this.translateService.instant('Turn on screenshare'),
          dataName: 'screenshare-checkbox',
        },
      ],
    },
    {
      name: this.translateService.instant('Board controls'),
      icon: 'edit',
      permissions: [
        {
          fieldName: 'createBoards',
          name: this.translateService.instant('Create boards'),
          dataName: 'create-board-checkbox',
        },
        {
          fieldName: 'editSpace',
          name: this.translateService.instant('Edit boards'),
          dataName: 'edit-boards-checkbox',
        },
      ],
    },
    {
      name: this.translateService.instant('Messaging'),
      icon: 'chat_bubble_outline',
      permissions: [
        {
          fieldName: 'messageEveryone',
          name: this.translateService.instant('Message in Space chat'),
          dataName: 'message-everyone-checkbox',
        },
        {
          fieldName: 'messageOtherParticipants',
          name: this.translateService.instant('Message other participants'),
          dataName: 'message-participants-checkbox',
        },
      ],
    },
    {
      name: this.translateService.instant('App controls'),
      svg: 'web-bottom-left-toolbar',
      hideForFreeUsers: true,
      permissions: [
        {
          fieldName: 'insertWebViewer',
          name: this.translateService.instant('Insert Web Viewer'),
          dataName: 'insert-web-viewer-checkbox',
          // Hide App Control expansion panel in case webviewer is not enabled.
          hidden: !this.isWebViewerCapabilityEnabled,
        },
      ],
    },
  ];

  isRequestAccessEnabled: boolean;
  allSettingsMenuOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  userSettingsMenuOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  openActionsMenuAsBottomSheet$: BehaviorSubject<{ val: boolean }> = new BehaviorSubject<{
    val: boolean;
  }>({ val: false });
  permissionsEnforcedByInstitution: { [key: string]: Observable<boolean> };

  private readonly DEBOUNCE_TIME = 5000;
  private domListener = this.domListenerFactoryService.createInstance();

  selectedUserForActions$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  /** Matches `$child-mode-flash-duration` in participants-manager.component.scss. */
  private static readonly CHILD_MODE_ROW_FLASH_MS = 1000;

  protected readonly childModeRowHighlighted$ = new BehaviorSubject<boolean>(false);
  private childModeRowHighlightClearHandle: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public attendanceManagerService: AttendanceManagerService,
    public sessionCallTracksService: SessionCallTracksService,
    public participantsManagerService: ParticipantsManagerService,
    public sessionSharedDataService: SessionSharedDataService,
    private realtimeSpaceService: RealtimeSpaceService,
    private userService: UserService,
    private flagsService: FlagsService,
    private cd: ChangeDetectorRef,
    private providerState: ProviderStateService,
    private presenceProvider: PresenceProvider,
    public spaceRepo: SpaceRepository,
    public rtcServiceController: RtcServiceController,
    public videoCallTracksStateService: VideoCallTracksStateService,
    private translateService: TranslateService,
    private notificationToasterService: NotificationToasterService,
    private localTracksManager: LocalTracksManagerService,
    private spacesService: SpacesService,
    public paywallIndicatorService: PaywallIndicatorService,
    public requestAccessService: RequestAccessService,
    public waitingRoomService: WaitingRoomService,
    private domListenerFactoryService: DomListenerFactoryService,
    private globalPermissionsService: GlobalPermissionsService,
    protected featureManagementService: FeatureManagementService,
    protected screenSizeService: ScreenSizeService,
    private cursorControlsService: CursorControlsService,
    private childModeService: ChildModeService,
    private spaceSettingsUpdateService: SpaceSettingsUpdateService,
    private raiseHandUiService: RaiseHandUiService,
    private callAndPresencesService: CallAndPresencesService,
  ) {
    this.areCallFeaturesAvailable = this.callAndPresencesService.areCallFeaturesAvailable();
    this.isRequestAccessEnabled = this.flagsService.isFlagEnabled(FLAGS.REQUEST_ACCESS_ENABLED);

    this.providerState.callConnected$.pipe(untilDestroyed(this)).subscribe((val) => {
      this.isCallConnected = val;
    });

    this.providerState.callReady$.pipe(untilDestroyed(this)).subscribe((val) => {
      this.isCallReady = val;
    });

    this.spaceRepo.spaceCursorsPermission$
      .pipe(untilDestroyed(this))
      .subscribe((cursorsControl) => {
        this._currentCursorsPermissson.next(cursorsControl || CursorsControl.AUTOMATIC);
      });

    combineLatest([
      this.spaceRepo.spaceVisibility$,
      this.globalPermissionsService.isWaitingRoomEnabled$,
      this.childModeService.isChildModeEnabled$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([visibility, isWaitingRoomEnabled, isChildModeEnabled]) => {
        this.selectedVisibility$.next(visibility);
        this.selectedWaitingRoom$.next(isWaitingRoomEnabled);
        this.selectedChildMode$.next(isChildModeEnabled);
        this.checkSecuritySectionChange();
      });
    // Follow the stored value when it changes (another host saved it, or our save
    // came back), the same way child mode does.
    this.spaceRepo.activeSpace$
      .pipe(
        map((space) => !!space?.settings?.allowExternalAgentMessages),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((allowed) => {
        this.selectedExternalAgentMessages$.next(allowed);
        this.checkSecuritySectionChange();
      });
    this.isWaitingRoomEnforced$ = this.globalPermissionsService.isWaitingRoomEnforced$;
    this.permissionsEnforcedByInstitution =
      this.globalPermissionsService.permissionsEnforcedByInstitution;
  }

  ngAfterViewInit(): void {
    const tabsContainer = document.getElementById('tabsDiv'); // replace with your container selector
    this.domListener.add(
      tabsContainer,
      'wheel',
      (event: any) => {
        event.preventDefault();
        tabsContainer && (tabsContainer.scrollLeft += event.deltaY + event.deltaX);
      },
      true,
    );
  }

  userDevicesStates$ = this.flagsService
    .featureFlagChanged(FLAGS.CALL_TRACKS_V2)
    .pipe(
      switchMap((enabled) =>
        enabled
          ? this.videoCallTracksStateService.usersDevicesStates$
          : this.sessionCallTracksService.usersDevicesStates$,
      ),
    );

  inCallUsers$ = combineLatest([
    this.spaceRepo.activeSpace$,
    this.spaceRepo.activeSpaceCurrentRoomUid$,
  ]).pipe(
    switchMap(([activeSpace, activeSpaceCurrentRoomUid]) =>
      this.presenceProvider.getCallPresenceActivity(
        activeSpace?._id as string,
        activeSpaceCurrentRoomUid,
      ),
    ),
    map((presentUsers) => presentUsers ?? new Set()),
    tap((presentUsers) => {
      this.inCallUsers = presentUsers ?? new Set();
      this.calcInCallParticipants();
    }),
  );

  viewModel$ = combineLatest([this.userDevicesStates$, this.inCallUsers$]).pipe(
    map(([userDevicesStates, inCallUsers]) => ({ userDevicesStates, inCallUsers })),
  );

  queueBadgeCount$ = combineLatest([
    this.requestAccessService.accessRequestersSize$,
    this.waitingRoomService.accessRequestersSize$,
    this.raiseHandUiService.raisedHandQueueCount$,
  ]).pipe(
    untilDestroyed(this),
    map(
      ([accessRequestersSize, waitingRoomSize, raisedCount]) =>
        accessRequestersSize + waitingRoomSize + raisedCount,
    ),
  );

  showQueueTab$ = combineLatest([
    this.requestAccessService.shouldShowAccessRequests$,
    this.waitingRoomService.shouldShowAccessRequests$,
    this.raiseHandUiService.raisedHandQueueCount$,
    this.queueBadgeCount$,
  ]).pipe(
    untilDestroyed(this),
    map(([shouldShowAccess, shouldShowWaiting, raisedCount, requestsCount]) => {
      const shouldShowRaisedHandQueue = raisedCount > 0;
      return (
        (shouldShowAccess || shouldShowWaiting || shouldShowRaisedHandQueue) && requestsCount > 0
      );
    }),
  );

  closePanel(): void {
    this.sessionSharedDataService.changeRightPanelView.next(undefined);
  }

  openSection(section: Section): void {
    // added so that listeners to this observable be updated
    this.sessionSharedDataService.changeParticipantsManagerSection.next(section);
  }

  ngOnInit(): void {
    if (!this.sessionSharedDataService.changeParticipantsManagerSection.value) {
      this.sessionSharedDataService.changeParticipantsManagerSection.next(Section.CONTROLS);
    }

    if (!this.currentUser) {
      this.subscribers.push(
        this.userService.user.pipe(untilDestroyed(this)).subscribe((res: UserData | null) => {
          if (res?.user) {
            this.currentUser = res.user;
          }
        }),
      );
    }
    combineLatest([this.spaceRepo.activeSpace$, this.spaceRepo.activeSpaceCurrentRoomUid$])
      .pipe(untilDestroyed(this))
      .subscribe(([activeSpace, activeSpaceCurrentRoomUid]) => {
        if (!activeSpace) {
          return;
        }

        if (!activeSpaceCurrentRoomUid) {
          return;
        }

        if (
          activeSpace._id !== this.sessionId ||
          activeSpaceCurrentRoomUid !== this.currentBreakoutRoomId
        ) {
          this.sessionId = activeSpace._id;
          this.currentBreakoutRoomId = activeSpaceCurrentRoomUid;
          this.handleSpaceOrBreakoutRoomChange();
        }

        this.isHost = Session.isOwnedByUser(activeSpace, this.userService.user?.value?.user);

        if (!this.isHost) {
          this.sessionSharedDataService.changeParticipantsManagerSection.next(Section.CONTROLS);
          this.selectedPermission = undefined;
          this.closePanel();
        }

        activeSpace.users?.forEach((user) => {
          this.rolesMap[user._id] = user;
        });

        activeSpace.populatedUsers?.forEach((user) => {
          this.usersMap[user._id] = user;
        });
        this.activeRoomUsers = activeSpace.users?.filter(
          (user) =>
            !!this.usersMap[user._id] &&
            this.currentUser?._id !== user._id &&
            user.roomId === activeSpaceCurrentRoomUid &&
            !user.isOwner,
        );
        this.calcInCallParticipants();
        this.updatePresentUsers();
      });
    this.listenToCurrentRoom();

    this.queueBadgeCount$
      .pipe(untilDestroyed(this), distinctUntilChanged(), startWith(0), pairwise())
      .subscribe(([oldSize, newSize]) => {
        if (
          oldSize !== 0 &&
          newSize === 0 &&
          this.sessionSharedDataService.changeParticipantsManagerSection.value === Section.QUEUE
        ) {
          // size dropped to 0 --> requests tab will disappear and go to controls tab
          this.sessionSharedDataService.changeParticipantsManagerSection.next(Section.CONTROLS);
        }
      });

    this.childModeService.flashChildModePermissionsRow.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.childModeRowHighlightClearHandle !== undefined) {
        clearTimeout(this.childModeRowHighlightClearHandle);
      }
      this.childModeRowHighlighted$.next(true);

      this.childModeRowHighlightClearHandle = modifiedSetTimeout(() => {
        this.childModeRowHighlighted$.next(false);
        this.childModeRowHighlightClearHandle = undefined;
      }, ParticipantsManagerComponent.CHILD_MODE_ROW_FLASH_MS);
    });
  }

  private handleSpaceOrBreakoutRoomChange() {
    this.listenForSpacePresence();
  }

  listenForSpacePresence(): void {
    this.presenceSub?.unsubscribe();
    this.presenceSub = this.presenceProvider
      .getRoomPresenceActivity(this.sessionId, this.currentBreakoutRoomId)
      .pipe(untilDestroyed(this))
      .subscribe((presentUsers) => {
        this.currentPresentUsers = presentUsers ?? new Set();
        this.updatePresentUsers();
      });
  }

  listenToCurrentRoom(): void {
    combineLatest([
      this.spaceRepo?.activeSpaceCurrentRoom$,
      this.globalPermissionsService.spacePermissions$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([room, spacePermissions]) => {
        if (room) {
          // cancel permissions update to revert any unsaved changes if another update request happened
          this.cancelPermissionUpdate();
          this.currentRoom = cloneDeep(room);
          if (this.currentRoom.permissions) {
            // set currentPermissions from the room and override the permissions that we get from DB
            this.currentPermissions = this.globalPermissionsService.mergeRoomAndSpacePermissions(
              this.currentRoom.permissions,
              spacePermissions,
            );
            const defaultPermissions = new Permissions();
            Object.keys(new Permissions()).forEach((permission) => {
              if (typeof this.currentPermissions[permission] === 'undefined') {
                this.currentPermissions[permission] =
                  defaultPermissions[permission as keyof Permissions];
              }
            });
            this.originalPermissions = {
              ...defaultPermissions,
              ...cloneDeep(this.currentPermissions),
            };
          }
          this.permissionsChanged = false;
          this.cd.detectChanges();
        }
      });
  }

  updateParticipantsPermissions(session: Session | ISession, newPermissions: Permissions): void {
    session?.users.forEach((user) => {
      if (!user.isOwner) {
        user.userPermissions = { ...newPermissions };
      }
    });
    this.spaceRepo.updateSpace(session._id, session);
  }

  updatePresentUsers(): void {
    this.activeHostUsers = [];
    this.activeParticipantsUsers = [];
    this.inactiveUsers = [];
    this.spaceRepo.activeSpace?.populatedUsers?.forEach((userObject) => {
      if (this.currentPresentUsers.has(userObject._id)) {
        if (this.rolesMap[userObject._id]?.isOwner) {
          this.activeHostUsers.push(userObject);
        } else {
          this.activeParticipantsUsers.push(userObject);
        }
      } else {
        this.inactiveUsers.push(userObject);
      }
    });
    this.activeHostUsers.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else {
        return 1;
      }
    });
    this.activeParticipantsUsers.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      } else {
        return 1;
      }
    });
    this.cd.markForCheck();
  }

  toggleDevice(device: DEVICE): void {
    switch (device) {
      case DEVICE.CAM:
        this.localTracksManager.toggleDeviceState(DeviceType.VIDEO, true);
        break;
      case DEVICE.MIC:
        this.localTracksManager.toggleDeviceState(DeviceType.AUDIO, true);
        break;
    }
  }

  calcInCallParticipants(): void {
    let inCallParticipants = 0;
    for (const userId of this.inCallUsers) {
      if (!this.rolesMap[userId]?.isOwner) {
        inCallParticipants += 1;
      }
    }
    this.inCallParticipants = inCallParticipants;
  }

  ngOnDestroy(): void {
    this.subscribers.forEach((sub) => sub.unsubscribe());
  }

  turnOffDeviceForAllParticipants(device: string): void {
    if (!this.isHost || !this.isCallConnected) {
      return;
    }

    const event = new SessionEvent(
      EventCategory.ParticipantsManager,
      ParticipantsManagerEvent.TURNOFF_ALL,
      { device },
    );
    this.realtimeSpaceService.service.sendEvent(this, event);
  }

  requestAllParticipantsToTurnOnDevice(device: DEVICE): void {
    if (!this.isHost || !this.isCallConnected) {
      return;
    }

    const event = new SessionEvent(
      EventCategory.ParticipantsManager,
      ParticipantsManagerEvent.TURN_ON_REQUEST_ALL,
      { device },
    );
    this.realtimeSpaceService.service.sendEvent(this, event);

    this.showRequestAllParticipantsToTurnOnDeviceSentNotification(device);
  }

  showRequestAllParticipantsToTurnOnDeviceSentNotification(device: DEVICE): void {
    if (![DEVICE.MIC, DEVICE.CAM].includes(device)) {
      return;
    }
    const topElement = new IconMessageToasterElement(
      { icon: 'check', size: 16 },
      this.translateService.instant('Request sent'),
    );

    const messages = {
      [DEVICE.CAM]: this.translateService.instant(
        'We’ve notified all participants to turn on their cameras',
      ),
      [DEVICE.MIC]: this.translateService.instant(
        'We’ve notified all participants to turn on their microphones',
      ),
    };
    const middleElement = new IconMessageToasterElement(undefined, messages[device]);

    const codes = {
      [DEVICE.CAM]: SUCCESSES.REQUEST_ALL_TO_TURN_ON_CAMERA_SENT,
      [DEVICE.MIC]: SUCCESSES.REQUEST_ALL_TO_UNMUTE_SENT,
    };

    const notificationDataBuilder = new NotificationDataBuilder(codes[device])
      .type(NotificationType.SUCCESS)
      .style(ToasterPopupStyle.SUCCESS)
      .showProgressBar(true)
      .priority(380)
      .timeOut(5)
      .topElements([topElement])
      .middleElements([middleElement]);

    this.notificationToasterService.showNotification(notificationDataBuilder.build());
  }

  sendPermissionUpdates(): void {
    if (this.securitySectionChanged) {
      this.applyChangeVisibility();
      this.applyToggleWaitingRoom();
      this.applyToggleChildMode();
      this.applyToggleExternalAgentMessages();
    }

    if (this.isCursorPermissionChanged) {
      this.applyChangeCursorsControl();
    }
    if (!this.permissionsChanged) {
      return;
    }
    if (this.currentRoom) {
      this.updateSessionPermissions(this.currentRoom.permissions);
      this.realtimeSpaceService.service.modifyRoom(this.currentRoom, this.sessionId);
    }
    this.permissionsChanged = false;
  }

  /**
   * update space permissions in DB
   * the filter get the modified permissions stored in DB only to avoid unnecessary back end calls
   */
  updateSessionPermissions(newPermissions: Permissions): void {
    const filteredPermissions = this.globalPermissionsService.filterPermissionsFromDB(
      newPermissions,
      this.originalPermissions,
    );
    if (Object.keys(filteredPermissions).length === 0) {
      this.spaceSettingsUpdateService.showPermissionsUpdatedSuccess();
      return;
    }
    const spaceID = this.spaceRepo.activeSpace?._id;
    if (!spaceID) {
      return;
    }
    this.spacesService
      // update the filtered permissions only in DB
      .updateSessionPermissions(spaceID, filteredPermissions, true)
      .pipe(
        take(1),
        catchError((error) => this.handleInstitutionPermissionsUpdateError()),
      )
      .subscribe({
        next: (res) => {
          this.spaceRepo.updateSpace(spaceID, { sessionPermissions: newPermissions });
          this.checkPermissionChange();
          this.spaceSettingsUpdateService.showPermissionsUpdatedSuccess();
        },
        error: (err) => {},
      });
  }

  cancelPermissionUpdate() {
    if (this.isCursorPermissionChanged) {
      this.changeCursorsControl(this.spaceRepo.spaceCursorsPermission);
    }

    if (this.securitySectionChanged) {
      this.selectedVisibility$.next(this.spaceRepo.activeSpace?.visibility);
      this.selectedWaitingRoom$.next(this.globalPermissionsService.isWaitingRoomEnabled);
      this.selectedChildMode$.next(!!this.spaceRepo.activeSpace?.settings?.enableChildMode);
      this.selectedExternalAgentMessages$.next(
        !!this.spaceRepo.activeSpace?.settings?.allowExternalAgentMessages,
      );
      this.securitySectionChanged = false;
    }
    if (!this.permissionsChanged) {
      return;
    }
    this.permissionsChanged = false;
    this.currentPermissions = cloneDeep(this.originalPermissions);
    this.permissionsGroupMetadata = cloneDeep(this.permissionsGroupMetadata);
  }

  updateIndividualPermission(event: MatCheckboxChange, user: SessionUser) {
    if (this.selectedPermission) {
      if (typeof this.currentPermissions[this.selectedPermission.fieldName] === 'boolean') {
        const value = this.currentPermissions[this.selectedPermission.fieldName];
        this.currentPermissions[this.selectedPermission.fieldName] = {};
        this.activeRoomUsers.forEach((roomUser) => {
          if (this.selectedPermission) {
            this.currentPermissions[this.selectedPermission.fieldName][roomUser._id] = value;
          }
        });
      }
      this.currentPermissions[this.selectedPermission.fieldName][user._id] = event.checked;
      this.currentPermissions = getUpdatedPermissions(
        this.currentPermissions,
        this.selectedPermission.fieldName,
      );
      this.checkPermissionChange();
    }
  }

  updateEveryonePermission(event: MatCheckboxChange, selectedPermission: string) {
    this.currentPermissions[selectedPermission] = event.checked;
    this.checkPermissionChange();
  }

  checkPermissionChange() {
    this.permissionsChanged = !isEqual(this.originalPermissions, this.currentPermissions);
  }

  checkSecuritySectionChange() {
    this.securitySectionChanged =
      this.selectedVisibility$.value !== this.spaceRepo.activeSpace?.visibility ||
      this.selectedWaitingRoom$.value !== this.globalPermissionsService.isWaitingRoomEnabled ||
      this.selectedChildMode$.value !==
        (this.spaceRepo.activeSpace?.settings?.enableChildMode === true) ||
      this.selectedExternalAgentMessages$.value !==
        (this.spaceRepo.activeSpace?.settings?.allowExternalAgentMessages === true);
  }

  changeVisibility(visibility: Visibility) {
    this.selectedVisibility$.next(visibility);
    this.checkSecuritySectionChange();
  }

  toggleWaitingRoom(event: MatCheckboxChange) {
    this.selectedWaitingRoom$.next(event.checked);
    this.checkSecuritySectionChange();
  }

  applyChangeVisibility() {
    const visibility = this.selectedVisibility$.value;
    if (!visibility || visibility === this.spaceRepo.activeSpace?.visibility) {
      return;
    }
    const spaceID = this.spaceRepo.activeSpace?._id;
    if (!spaceID) {
      return;
    }

    this.spacesService
      .updateVisibility(spaceID, visibility)
      .pipe(timeout(this.DEBOUNCE_TIME), first())
      .subscribe({
        next: (res) => {
          this.spaceRepo.updateSpace(spaceID, { visibility: visibility });
          this.checkSecuritySectionChange();
          this.spaceSettingsUpdateService.showPermissionsUpdatedSuccess();
        },
        error: (err) => {
          this.checkSecuritySectionChange();
        },
      });
  }

  applyChangeCursorsControl() {
    if (!this.isCursorPermissionChanged) {
      return;
    }

    this.cursorControlsService.applyChangeCursorsControl(this._currentCursorsPermissson.value);
  }

  applyToggleWaitingRoom() {
    const newIsWaitingRoomEnabled = this.selectedWaitingRoom$.value;
    if (newIsWaitingRoomEnabled === this.globalPermissionsService.isWaitingRoomEnabled) {
      return;
    }
    this.spaceSettingsUpdateService.updateSettingsAndNotify(
      { enableWaitingRoom: newIsWaitingRoomEnabled },
      () => this.checkSecuritySectionChange(),
      (err) => this.handleWaitingRoomInstitutionPermissionsError(err),
    );
  }

  toggleChildMode(enabled: boolean): void {
    this.selectedChildMode$.next(enabled);
    this.checkSecuritySectionChange();
  }

  toggleExternalAgentMessages(allowed: boolean): void {
    this.selectedExternalAgentMessages$.next(allowed);
    this.checkSecuritySectionChange();
  }

  applyToggleExternalAgentMessages(): void {
    const allowed = this.selectedExternalAgentMessages$.value;
    if (allowed === !!this.spaceRepo.activeSpace?.settings?.allowExternalAgentMessages) {
      return;
    }
    this.spaceSettingsUpdateService.updateSettingsAndNotify(
      { allowExternalAgentMessages: allowed },
      () => this.checkSecuritySectionChange(),
    );
  }

  applyToggleChildMode(): void {
    const newIsChildModeEnabled = this.selectedChildMode$.value;
    if (newIsChildModeEnabled === this.spaceRepo.activeSpace?.settings?.enableChildMode) {
      return;
    }
    this.spaceSettingsUpdateService.updateSettingsAndNotify(
      { enableChildMode: newIsChildModeEnabled },
      () => this.checkSecuritySectionChange(),
    );
  }
  /**
   * handle unauthorized waiting room update error due to changes in institution permissions.
   * update the current session with latest version of institution permissions.
   */
  private handleWaitingRoomInstitutionPermissionsError(error: any) {
    if (error.error.institutionPermissions) {
      const activeSpace = this.spaceRepo.activeSpace;
      if (activeSpace?.institution) {
        const updatedInstitution: Institution = {
          ...activeSpace.institution,
          permissions: { ...(error.error.institutionPermissions ?? {}) },
        };
        this.spaceRepo.updateSpace(activeSpace._id, { institution: updatedInstitution });
        this.selectedWaitingRoom$.next(this.globalPermissionsService.isWaitingRoomEnabled);
      }
      this.showInstitutionPermissionsUpdateNotification();
    }
    this.checkSecuritySectionChange();
    return EMPTY;
  }

  /**
   * for now we are not handling edge cases for session permissions and just
   * show the warning notification to the host
   */
  private handleInstitutionPermissionsUpdateError() {
    this.showInstitutionPermissionsUpdateNotification();
    this.checkPermissionChange();
    return EMPTY;
  }

  private showInstitutionPermissionsUpdateNotification() {
    const notificationTitle = this.translateService.instant('Permissions update failed');
    const notificationIcon: NotificationIconConfig = {
      assetName: 'warning_amber_outline',
      type: NotificationIconTypes.SVG_ICON,
      width: 18,
      height: 18,
      background: IconBackground.WARNING,
    };
    const notificationDescription = this.translateService.instant(
      'You cannot update this permission because it has now been set by your institution and cannot be modified',
    );

    const notificationData = new V2NotificationDataBuilder(WARNINGS.INSTITUTION_PERMISSIONS_UPDATED)
      .type(NotificationType.WARNING)
      .style(ToasterPopupStyle.WARN)
      .title(notificationTitle)
      .icon(notificationIcon)
      .description(notificationDescription)
      .dismissable(true)
      .priority(560)
      .timeOut(5)
      .onActivateTick(true)
      .showProgressBar(true)
      .build();
    this.notificationToasterService.showNotification(notificationData);
  }

  onPermissionClick(permission: any) {
    // if the permission in not controlled by the institution
    if (!this.globalPermissionsService.isPermissionEnforcedByInstitution(permission.fieldName)) {
      this.selectedPermission = permission;
    }
  }

  onActionsMenuOpened(event: MouseEvent, selectedUser: User): void {
    event.stopPropagation();
    event.preventDefault();
    this.selectedUserForActions$.next(selectedUser);

    const openMenuAsBottomSheet = this.screenSizeService.renderMenuAsBottomSheet;
    this.openActionsMenuAsBottomSheet$.next({ val: openMenuAsBottomSheet });
  }

  changeCursorsControl(cursorsControl: CursorsControl) {
    this._currentCursorsPermissson.next(cursorsControl);
  }

  get isCursorPermissionChanged(): boolean {
    return this._currentCursorsPermissson.value !== this.spaceRepo.spaceCursorsPermission;
  }
}
